import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { SortDirection } from '../../../enums/sortDirection'
import { usePaginationPrefetching } from '../../../hooks/usePaginationPrefetching'
import { Paging } from '../../../types/metable'
import { ContactsSortProperty, fetchContactsAndContactPersons, FetchContactsResponseData } from '../query-api'
import { getContactsQueryKey } from '../utils/getContactsQueryKey'

export interface ContactsQueryProps {
  isArchived?: boolean
  isCustomer?: boolean
  isSupplier?: boolean
  organizationId?: string
  page?: number
  pageSize?: number
  q?: string
  sortDirection?: SortDirection
  sortProperty?: ContactsSortProperty
}

interface UseFetchContactsProps {
  isArchived?: boolean
  isCustomer?: boolean
  isSupplier?: boolean
  page?: number
  pageSize?: number
  searchQuery?: string
  sortDirection?: SortDirection
  sortProperty?: ContactsSortProperty
}

export const useFetchContacts = ({
  isArchived,
  isCustomer,
  isSupplier,
  page,
  pageSize,
  searchQuery,
  sortDirection,
  sortProperty,
}: UseFetchContactsProps) => {
  const { organization } = useUserOrganization()
  const previousPagination = useRef<Paging | undefined>(undefined)

  const queryProps: ContactsQueryProps = useMemo(() => {
    return {
      ...(page ? { page } : {}),
      ...(pageSize ? { pageSize } : {}),
      ...(searchQuery ? { q: searchQuery } : {}),
      ...(sortDirection ? { sortDirection } : {}),
      ...(sortProperty ? { sortProperty } : {}),
      organizationId: organization?.id as string,
      isArchived,
      isCustomer,
      isSupplier,
    }
  }, [isArchived, isCustomer, isSupplier, organization?.id, page, pageSize, searchQuery, sortDirection, sortProperty])

  const queryKey = useMemo(() => getContactsQueryKey(queryProps), [queryProps])

  const { data, isLoading, isIdle, ...rest } = useQuery(queryKey, () => fetchContactsAndContactPersons(queryProps), {
    enabled: !!organization,
  })

  // If pagination exists then copy the value to reference and use it when original pagination during loading returns `undefined` value.
  useEffect(() => {
    if (data?.meta.paging) {
      previousPagination.current = { ...data?.meta.paging }
    }
  }, [data?.meta.paging])

  usePaginationPrefetching<ContactsQueryProps, FetchContactsResponseData>({
    getQueryFunction: useMemo(() => fetchContactsAndContactPersons, []),
    getQueryKey: useMemo(() => getContactsQueryKey, []),
    isEnabled: !!organization?.id && !isLoading && !isIdle,
    queryProps,
    totalPages: data?.meta.paging.pageCount as number,
  })

  return {
    contacts: data?.contacts || [],
    isLoading: isIdle || isLoading,
    pagination: data?.meta.paging || previousPagination.current,
    ...rest,
  }
}
